import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InvoicesService } from '@/api';
import type { UpdateInvoiceRequest } from '@/types/billing';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react/macro';
import { invalidateMoneyViews } from '@/hooks/reconciliation/invalidateMoneyViews';
import { errorToastText } from '@/lib/translateBackendError';
import { transitionErrorText } from '@/lib/alreadyHandled';

// Query key factory for invoice cache invalidation
export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) => [...invoiceKeys.lists(), filters] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
  byPeriod: (year: number, month: number) => [...invoiceKeys.all, 'period', year, month] as const,
};

export function useUpdateInvoice() {
  const queryClient = useQueryClient();
  const { i18n, t } = useLingui();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateInvoiceRequest }) => {
      return await InvoicesService.putApiV1Invoices(id, data);
    },
    onSuccess: async (data) => {
      // Invalidate all invoice queries to refresh the UI
      // Cancel first: a read already in flight when the write committed
      // would otherwise resolve afterwards and repopulate the cache with
      // the pre-correction invoice. See invalidateMoneyViews.
      await queryClient.cancelQueries({ queryKey: invoiceKeys.all });
      await invalidateMoneyViews(queryClient);
      toast.success(t`invoices.success.updated`);
      return data;
    },
    onError: (error) => {
      toast.error(errorToastText(error, i18n, t`invoices.error.updateFailed`));
    },
  });
}

export function useAddSubsidyCorrection() {
  const queryClient = useQueryClient();
  const { t } = useLingui();

  return useMutation({
    mutationFn: async ({
      id,
      amountRappen,
      description,
    }: {
      id: string;
      amountRappen: number;
      description: string;
    }) => {
      return await InvoicesService.postApiV1InvoicesSubsidyCorrection(id, {
        amount_rappen: amountRappen,
        description,
      });
    },
    onSuccess: async (data) => {
      // Cancel first: a read already in flight when the write committed
      // would otherwise resolve afterwards and repopulate the cache with
      // the pre-correction invoice. See invalidateMoneyViews.
      await queryClient.cancelQueries({ queryKey: invoiceKeys.all });
      await invalidateMoneyViews(queryClient);
      toast.success(t`invoices.subsidyCorrection.success`);
      return data;
    },
    onError: (error) => {
      // Log the raw error for diagnostics; never surface a backend message to the
      // user — the toast always shows a localized, generic key.
      console.error('Failed to apply subvention correction:', error);
      toast.error(t`invoices.subsidyCorrection.error`);
    },
  });
}

export function useRegenerateInvoice() {
  const queryClient = useQueryClient();
  const { i18n, t } = useLingui();

  return useMutation({
    mutationFn: async (id: string) => {
      return await InvoicesService.postApiV1InvoicesRegenerate(id);
    },
    onSuccess: async (data) => {
      // Invalidate all invoice queries to refresh the UI
      // Cancel first: a read already in flight when the write committed
      // would otherwise resolve afterwards and repopulate the cache with
      // the pre-correction invoice. See invalidateMoneyViews.
      await queryClient.cancelQueries({ queryKey: invoiceKeys.all });
      await invalidateMoneyViews(queryClient);
      toast.success(i18n._(t`invoices.success.regenerated`));
      return data;
    },
    onError: (error) => {
      // A known code (e.g. SETTLING_IN_NOT_CLOSED, #1697) is shown localised,
      // then the backend's client-safe text; a lost race reads "Already handled".
      toast.error(transitionErrorText(error, i18n, i18n._(t`invoices.error.regenerateFailed`)));
    },
  });
}

/**
 * Correct (re-issue) a finalised+ invoice: cancels it and creates a corrected
 * DRAFT for the same contract + period. Works for departed children (terminated
 * contracts). Returns the new draft; the list is invalidated so it appears.
 */
export function useCorrectInvoice() {
  const queryClient = useQueryClient();
  const { i18n, t } = useLingui();

  return useMutation({
    mutationFn: async (id: string) => {
      return await InvoicesService.postApiV1InvoicesCorrect(id);
    },
    onSuccess: async (data) => {
      // Cancel first: a read already in flight when the write committed
      // would otherwise resolve afterwards and repopulate the cache with
      // the pre-correction invoice. See invalidateMoneyViews.
      await queryClient.cancelQueries({ queryKey: invoiceKeys.all });
      await invalidateMoneyViews(queryClient);
      toast.success(i18n._(t`invoices.success.corrected`));
      return data;
    },
    onError: (error) => {
      // Correction failures are admin-actionable (e.g. "the contract's dates
      // don't cover this period", or "no contract version covers it"): surface
      // the backend's sanitized message so the admin knows what to fix, falling
      // back to a generic localized key when no message is present.
      console.error('Failed to correct invoice:', error);
      toast.error(transitionErrorText(error, i18n, i18n._(t`invoices.error.correctFailed`)));
    },
  });
}
